import { Box, Title, Button, DropDown, MergedScheduledPayment } from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import useFileDownload from '@/hooks/useFiledDownload';
import ScheduledTransactionOverview from './ScheduledTransactionOverview';
import useCancelScheduledTransaction from '@/hooks/useApi/useCancelScheduledTransaction';
import useFetchScheduledPayments from '@/hooks/useApi/useFetchScheduledPayments';
import TransactionsHistory from './Transactions/TransactionHistory';
import styles from '../ScheduledTransactions.module.scss';

import CancelTransactionDialogue from '../../Dialogue/CancelTransaction/CancelTransactionDialogue';
import CancelTransactionContext, {
  CancelTransactionCtx,
} from '../../Dialogue/CancelTransaction/CancelTransactionContext';
import { CancelTransactionDialogueHandler, DialogueHandlerProps } from '../../Dialogue/types';
import { useTranslation } from 'react-i18next';
import { Skeleton, UtilButton } from 'UI';

type FC = FunctionComponent;

const SkeletonSection = () => (
  <Box flx="col" mt="md" gap="sm">
    <Skeleton width="180px" height="28px" />
    <Skeleton height="30px" />
    <Skeleton height="30px" />
    <Skeleton height="30px" />
  </Box>
);

type DataProps = {
  transactionId: string;
  activeModule: string;
  hasNewTransactionButton: boolean;
  // Set by the list pages so the details fetch queries the right source
  // (legacy and new schedule IDs are independent auto-increments)
  isLegacy?: boolean;
};
type URLParams = {
  TransactionID: string;
};

const ScheduledTransactionDetailsContainer: FC = () => {
  const { state } = useLocation<DataProps>();
  const { TransactionID } = useParams<URLParams>();
  const { onSubNavClick } = useNavMenu();
  const { printFile, downloadPdfPage } = useFileDownload();
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [hasNewStatus, setNewStatus] = useState(true);
  const { permissions } = usePermissions();
  const [showCancelTransactionConfirmAlert, setShowCancelTransactionConfirmAlert] = useState(false);
  const [showCancelTransactionErrorAlert, setShowCancelTransactionErrorAlert] = useState(false);
  const [sendCancelTransactionError, setSendCancelTransactionError] = useState('');
  const { cancelScheduledTransaction } = useCancelScheduledTransaction();
  const { fetching, fetchScheduledPaymentDetails } = useFetchScheduledPayments();
  const [scheduledPayment, setScheduledPayment] = useState<MergedScheduledPayment>();
  const { t } = useTranslation('translations', { keyPrefix: 'ScheduledTransactions.Details' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const history = useHistory();
  const alertDialog = useAlertDialog();

  const CancelTransaction: CancelTransactionCtx = {
    showCancelTransactionConfirmAlert,
    showCancelTransactionErrorAlert,
    sendCancelTransactionError,
  };

  const handlers = {
    handleCancelTransactionDialogue: (handlerProps: DialogueHandlerProps<CancelTransactionDialogueHandler>) => {
      const { type, state: handlerState, action = 'default' } = handlerProps;

      const dialogueHandlers: CancelTransactionDialogueHandler = {
        alert: {
          success: {
            default: async () => {
              setShowCancelTransactionConfirmAlert(false);
              if (!scheduledPayment) return;

              try {
                await cancelScheduledTransaction(scheduledPayment);
                setNewStatus(true);
              } catch (error) {
                setShowCancelTransactionErrorAlert(true);
                setSendCancelTransactionError(error instanceof Error ? error.message : '');
              }
            },
          },
          fail: {
            default: () => {
              setShowCancelTransactionConfirmAlert(false);
            },
          },
        },
        error: {
          success: {
            default: () => {},
          },
          fail: {
            default: () => {
              setShowCancelTransactionErrorAlert(false);
            },
          },
        },
      };

      const dialogueState = dialogueHandlers[type][handlerState];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  useEffect(() => {
    if (!hasNewStatus) return;

    async function loadScheduledPayment() {
      try {
        setScheduledPayment(await fetchScheduledPaymentDetails(TransactionID, state?.isLegacy));
      } catch (error) {
        alertDialog.error(
          tGlobal('error', { defaultValue: 'Error' }),
          error instanceof Error ? error.message : String(error),
        );
      } finally {
        setNewStatus(false);
      }
    }

    loadScheduledPayment();
  }, [TransactionID, hasNewStatus]);

  useEffect(() => {
    if (isExportingPDF) {
      downloadPdfPage('scheduledTransactions', 'scheduled-transactions');
    }

    if (isPrinting) {
      printFile('scheduledTransactions');
    }

    setIsExportingPDF(false);
    setIsPrinting(false);
  }, [isExportingPDF, isPrinting]);

  return (
    <CancelTransactionContext.Provider value={CancelTransaction}>
      <div id="scheduledTransactions">
        <Box flx="col" gapSize="16px">
          <UtilButton
            iconLeft="chevron_left"
            size="large"
            onClick={() => {
              if (history.length > 1) {
                history.goBack();
              } else {
                history.replace('/scheduled-transactions');
              }
            }}
          >
            {tGlobal('Buttons.back')}
          </UtilButton>

          <Box width="100%" flx="row" jc="sb">
            <Box mb="md" jc="fs" ai="center" gap="common">
              <Title title={t('title')} />

              <div className="hiddenFormsPrint">
                {state?.hasNewTransactionButton && (
                  <Button
                    id="start-new-transactions"
                    label={t('startNewTransactionButton')}
                    onClick={() => {
                      if (state.activeModule === 'fundMyAccount') {
                        onSubNavClick('fundMyAccount', '/fund-my-account', 'Fund My Account');
                      } else if (state.activeModule === 'bankAccountTransfer') {
                        onSubNavClick('bankAccountTransfer', '/bank-account-transfer', 'Bank Account Transfer');
                      } else if (state.activeModule === 'withdrawMyAccount') {
                        onSubNavClick('withdrawMyAccount', '/withdraw-my-account', 'Withdraw My Account');
                      } else if (state.activeModule === 'singleCollection') {
                        onSubNavClick('singleCollection', '/single-collection', 'Collection');
                      } else if (state.activeModule === 'singlePayment') {
                        onSubNavClick('singlePayment', '/single-payment', 'Payment');
                      } else if (state.activeModule === 'accountTransfer') {
                        onSubNavClick('accountTransfer', '/sub-account-transfer', 'Account Transfer');
                      }
                    }}
                  />
                )}
              </div>
            </Box>

            <div className="hiddenFormsPrint">
              <Box flx="row" gap="sm">
                {scheduledPayment?.Status === 'in progress' && permissions.ManageTransactions && (
                  <Button
                    id="cancel-scheduled=transaction"
                    label={t('cancelTransactionButton')}
                    color="secondary"
                    onClick={() => {
                      setShowCancelTransactionConfirmAlert(true);
                    }}
                  />
                )}

                <div className={styles.dropdownContainer}>
                  <Button
                    id="export-transactions"
                    color="none"
                    label={t('ExportDropdown.export')}
                    height="42px"
                    textSize={1}
                    background="transparent"
                    noShadow
                    icon="arrowDown"
                    iconLocation="right"
                    onClick={() => {
                      setIsMenuVisible(true);
                    }}
                  />

                  {isMenuVisible && (
                    <DropDown isMenuVisible={isMenuVisible} onClick={setIsMenuVisible}>
                      <Button
                        id="export-PDF-btn"
                        label={t('ExportDropdown.Options.pdf')}
                        background="transparent"
                        textSize={1}
                        width="100%"
                        noShadow
                        onClick={() => {
                          setIsExportingPDF(true);
                        }}
                      />

                      <Button
                        id="export-Print-btn"
                        label={t('ExportDropdown.Options.print')}
                        background="transparent"
                        textSize={1}
                        width="100%"
                        noShadow
                        onClick={() => {
                          setIsPrinting(true);
                        }}
                      />
                    </DropDown>
                  )}
                </div>
              </Box>
            </div>
          </Box>

          {fetching && !scheduledPayment && (
            <Box flx="col" gapSize="16px" mt="md">
              <SkeletonSection />
              <SkeletonSection />
            </Box>
          )}

          {scheduledPayment && (
            <>
              <ScheduledTransactionOverview transaction={scheduledPayment} />
              <TransactionsHistory transaction={scheduledPayment} setNewStatus={setNewStatus} />
            </>
          )}
        </Box>
      </div>

      <CancelTransactionDialogue handleDialogue={handlers.handleCancelTransactionDialogue} />
    </CancelTransactionContext.Provider>
  );
};

export default ScheduledTransactionDetailsContainer;
